import "./ImageCanvas.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Buffer} from "buffer";
import {IDropFileModel} from "../../types/common";
import {Badge} from "react-bootstrap";
import {MAX_HEIGHT, MAX_WIDTH} from "../../utils/imageMatcherUtil";
import {uploadImage} from "../../utils/storageUtil";
import {queryItemFromPinecone} from "../../services/imageMatcher/queryItemFromPinecone";
import {useConfigState} from "../../hooks";

interface ImageCanvasProps {
    dropFile: IDropFileModel;
    openedImages: string[];
    setOpenedImages: (imageIds: string) => void;
    onProcessedImage: (image: IDropFileModel) => void;
}

export const ImageCanvas: React.FC<ImageCanvasProps> = ({
                                                            dropFile,
                                                            openedImages,
                                                            setOpenedImages,
                                                            onProcessedImage
                                                        }) => {
    const canvRef = useRef<HTMLCanvasElement>(null);
    const {t} = useTranslation();
    const [scale, setScale] = useState<number>(1);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isProcessed, setIsProcessed] = useState<boolean>(false);
    const {top_k, databaseNamespaces} = useConfigState();
    const namespace = databaseNamespaces.value === "BTL" ? "Part Position" : null;

    useEffect(() => {
        if (!dropFile.fileobject) return;

        const image = new Image();
        const objectUrl = URL.createObjectURL(dropFile.fileobject);
        image.src = objectUrl;

        image.onload = () => {
            const yScale = MAX_HEIGHT / image.naturalHeight;
            const xScale = MAX_WIDTH / image.naturalWidth;
            setScale(Math.min(yScale, xScale));
            setImageElement(image);
            setIsLoading(false);
        };

        return () => URL.revokeObjectURL(objectUrl);
    }, [dropFile.fileobject]);


    const processImage = useCallback(
        async (image: HTMLImageElement) => {
            if (!canvRef.current) return;
            const canvas = canvRef.current;
            canvas.width = image.naturalWidth * scale;
            canvas.height = image.naturalHeight * scale;
            const ctx = canvas.getContext("2d");

            if (ctx) {
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

                canvas.toBlob(async (blob) => {
                    if (!blob || dropFile.id) return setIsProcessed(true);

                    const buffer = Buffer.from(await blob.arrayBuffer());
                    const hash = await window.crypto.subtle.digest("SHA-256", buffer);
                    const hashHex = Array.from(new Uint8Array(hash))
                        .map((b) => b.toString(16).padStart(2, "0"))
                        .join("");

                    const filename = `image_similarity_search/${hashHex}.png`;
                    const newUploadFile = new File([blob], filename, {type: "image/png"});

                    dropFile.fileobject = newUploadFile;
                    const response = await uploadImage(newUploadFile);

                    if (response) {
                        dropFile.id = hashHex;
                        dropFile.pineconeResult = await queryItemFromPinecone({
                            dropFileId: dropFile.id,
                            key: filename,
                            topK: Number(top_k),
                            namespace: namespace
                        });
                        onProcessedImage(dropFile);
                        setIsProcessed(true);
                    }
                }, "image/png");
            }
        },
        [dropFile, onProcessedImage, scale, top_k]
    );

    useEffect(() => {
        if (imageElement) {
            processImage(imageElement)
        }
    }, [imageElement, processImage]);

    const handleOpenImage = () => {
        if (dropFile.id) {
            setOpenedImages(dropFile.id);
            window.open(`${window.location}/result/${dropFile.id}`, "_blank");
        }
    };

    return (
        <div className={`image-container ${openedImages.includes(dropFile.id ?? "") ? "opened" : ""}`}>
            <div className="canvas-wrapper">
                <canvas ref={canvRef} height={MAX_HEIGHT} width={MAX_WIDTH} className="image"></canvas>
                {isLoading || !isProcessed ? (
                    <div className="middle spinner">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">{t("DropZone.loading")}</span>
                        </div>
                    </div>
                ) : null}
            </div>

            {isProcessed && dropFile.id && (
                <>
                    {openedImages.includes(dropFile.id) && (
                        <Badge className="badge-custom" bg="success">
                            Opened
                        </Badge>
                    )}
                    <div className="middle">
                        <button
                            className="btn btn-secondary mt-2 show-result-button"
                            type="button"
                            onClick={handleOpenImage}
                        >
                            {t("DropZone.showResults")}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};
